<template>
  <section class="text-paragraph">
    <div class="text-paragraph__container">
      <!-- Bold Text Row -->
      <div
        v-if="boldText"
        class="text-paragraph__bold">
        <bold-text
          :text="boldText"
          color="red" />
      </div>

      <div
        v-if="preTitle"
        class="text-paragraph__pre-title">
        <span
          class="text-paragraph__pre-title-text"
          v-html="preTitle" />
      </div>

      <!-- Space-s gap -->
      <div
        v-if="boldText"
        class="text-paragraph__gap--s"></div>

      <!-- Title -->
      <div
        v-if="showTitle"
        class="text-paragraph__title">
        <h2
          class="text-paragraph__heading"
          v-html="title"></h2>
      </div>

      <!-- Space-l gap -->
      <div
        v-if="showTitle"
        class="text-paragraph__gap--l"></div>

      <!-- Space-s gap (when title is hidden) -->
      <div
        v-if="!showTitle"
        class="text-paragraph__gap--s"></div>

      <!-- Description Paragraph -->
      <div
        v-if="description"
        class="text-paragraph__description">
        <div
          class="text-paragraph__text"
          v-html="description"></div>
      </div>

      <!-- Space-m gap (only if CTAs are shown) -->
      <div
        v-if="showCtas"
        class="text-paragraph__gap--m"></div>

      <!-- CTAs Row (optional) -->
      <div
        v-if="showCtas"
        class="text-paragraph__ctas">
        <cta
          :text="primaryCtaText"
          :variant="ctaVariant"
          :to="primaryCtaTo"
          color="red"
          :show-icon="showCtaIcon"
          :animated-icon="showCtaIcon"
          @click="$emit('primary-cta-click')" />

        <cta
          :text="secondaryCtaText"
          :variant="ctaVariant"
          :to="secondaryCtaTo"
          color="red"
          :show-icon="showCtaIcon"
          :animated-icon="showCtaIcon"
          @click="$emit('secondary-cta-click')" />
      </div>
    </div>
  </section>
</template>

<script setup>
  import BoldText from './BoldText.vue'
  import Cta from './Cta.vue'

  // Props
  defineProps({
    boldText: {
      type: String
    },
    preTitle: {
      type: String,
      required: false
    },
    title: {
      type: String,
      required: false,
      default: '',
      validator: value => typeof value === 'string'
    },
    description: {
      type: String,
      required: false,
      default: ''
    },
    showTitle: {
      type: Boolean,
      default: true
    },
    showCtas: {
      type: Boolean,
      default: false
    },
    primaryCtaText: {
      type: String,
      default: 'Scopri di più'
    },
    secondaryCtaText: {
      type: String,
      default: 'Contattaci'
    },
    primaryCtaTo: {
      type: [
        String,
        Object,
        Array
      ]
    },
    secondaryCtaTo: {
      type: [
        String,
        Object,
        Array
      ]
    },
    ctaVariant: {
      type: String,
      default: 'solid',
      validator: value => [ 'solid', 'text' ].includes(value)
    },
    showCtaIcon: {
      type: Boolean,
      default: true
    }
  })

  // Emits
  defineEmits([ 'primary-cta-click', 'secondary-cta-click' ])
</script>

<style lang="scss" scoped>
.text-paragraph {
  width: 100%;

  &__container {
    @include page-x-padding;
  }

  &__bold {
    // BoldText container
  }

  &__pre-title {
    font-size: var(--typography-size-m, 20px);
    font-style: normal;
    line-height: 120%;
    letter-spacing: 0.4px;

    // Display and layout
    display: inline-block;
    overflow-wrap: break-word;
    color: var(--color-red);
    margin-bottom: 16px;

    // Responsive design
    @include mq($until: tablet) {
      font-size: var(--typography-size-ui-kit-18, 18px);
      letter-spacing: 0.3px;
    }

    @include mq($until: mobile) {
      font-size: var(--typography-size-ui-kit-16, 16px);
      letter-spacing: 0.2px;
    }
  }

  &__gap--s {
    height: var(--space-s);
  }

  &__gap--l {
    height: var(--space-l);
  }

  &__gap--m {
    height: var(--space-m);
  }

  &__title {
    // Title container
  }

  &__heading {
    color: var(--color-black);
    font-size: var(--text-t-l, 40px); // Using --text-t-l from _vars.scss
    font-style: normal;
    font-weight: 500;
    line-height: 140%;
    margin: 0;

    @include mq($until: tablet) {
      font-size: var(--text-t-m, 30px); // Responsive sizing
    }

    @include mq($until: mobile) {
      font-size: var(--text-t-s, 24px);
    }
  }

  &__description {
    // font-weight: 400;
    // font-size: var(--typography-size-m, 20px);
  }

  &__text {
    color: var(--color-black);
    font-size: var(--typography-size-m, 20px);
    font-style: normal;
    font-weight: 400;
    line-height: 160%;
    margin: 0;

    // Il contenuto arriva da CKEditor come <p>…</p>: azzera i margini
    // esterni mantenendo lo spazio tra paragrafi multipli.
    :deep(p) {
      margin: 0 0 1em;

      &:last-child {
        margin-bottom: 0;
      }
    }

    @include mq($until: tablet) {
      font-size: var(--text-b-n, 18px);
    }

    @include mq($until: mobile) {
      font-size: var(--text-b-s, 16px);
    }
  }

  &__ctas {
    display: flex;
    flex-direction: column;
    gap: var(--space-s);
    align-items: flex-start;

    @include mq(medium) {
      gap: var(--space-n);
      align-items: center;
    }
  }
}
</style>
